import {
  useCallback, useMemo, useRef, useState,
} from 'react';

import useMountedRef from './useMountedRef';
import { buildTutorials } from './tutorialSteps';
import {
  areAllTutorialsComplete,
  clearTutorialSession,
  isTutorialComplete,
  markTutorialComplete,
  markTutorialsDismissed,
  readProgress,
  rememberTutorialCompletion,
  resolveUserKey,
} from './tutorialProgress';
import { captureAnalytics } from '../../actions/preload';
import { tutorialAvailabilityContext } from './tutorialAvailability';

/**
 * What the user HAS done: per-user progress, the resolved checklist, and the
 * durable `users.has_completed_tutorial` summary flag.
 *
 * Split out of LearnMedsender (with useTutorialLifecycle, which owns a tour that
 * is *running*) so the container itself is wiring and render.
 */
const useTutorialProgress = ({
  userEmail,
  hasEmrIntegration,
  hasCompletedTutorial,
  impersonating,
  updateUserDetails,
  finishTutorial,
  closeTutorialMenu,
}) => {
  const mountedRef = useMountedRef();
  const userKey = resolveUserKey(userEmail);
  /**
   * The tutorial already completed by the CURRENT run, so one real action cannot
   * be recorded twice.
   *
   * Two paths finish a tutorial and both are wanted: the taught action reporting
   * success (`useTutorialLifecycle`'s `completesOn` listener, which has to write
   * synchronously before a page teardown), and the user walking the tour to its
   * end. For a tutorial that is both — send-fax, where clicking Send settles the
   * outcome while the tour is still showing its last step — they can land one
   * after the other. Storage writes were already idempotent, but the
   * `tutorial_completed` analytics event and the `finishTutorial` dispatch were
   * not, so one action produced two of each. Cleared by `beginTutorialRun` so
   * genuinely re-running a tutorial still counts.
   */
  const completedThisRunRef = useRef(null);
  const [progressOverride, setProgressOverride] = useState(null);
  const storedProgress = useMemo(() => readProgress(userEmail), [userEmail]);
  const progress = progressOverride && progressOverride.userKey === userKey
    ? progressOverride.value
    : storedProgress;

  // Availability inputs live in tutorialAvailability.js, shared with both Guide
  // surfaces so the panel and the Guide can never disagree about what is offered.
  const availability = tutorialAvailabilityContext();
  const { hasRecords, hasInboxRedesign } = availability;

  const tutorials = useMemo(() => (
    buildTutorials({ hasEmrIntegration, hasRecords, hasInboxRedesign }).map((tutorial) => ({
      ...tutorial,
      completed: isTutorialComplete(progress, tutorial.id),
    }))
  ), [hasEmrIntegration, hasRecords, hasInboxRedesign, progress]);

  const completedCount = tutorials.filter((tutorial) => tutorial.completed).length;

  // Only the core four drive the backend `has_completed_tutorial` flag and the
  // post-walkthrough auto-open. The optional extras are tracked locally like
  // everything else but never gate "done" — finishing the core checklist must
  // not be blocked by (or nag about) extras.
  const coreTutorials = useMemo(
    () => tutorials.filter((tutorial) => tutorial.core),
    [tutorials],
  );

  /** Writes the backend summary flag once the checklist is done or dismissed. */
  const persistTutorialCompletion = useCallback(async () => {
    if (impersonating || hasCompletedTutorial) return;
    try {
      await updateUserDetails({ has_completed_tutorial: true }, { rejectOnError: true });
    } catch {
      // Non-fatal: local progress still reflects reality and the only
      // consequence is that the auto-open may be re-evaluated next session.
    }
  }, [impersonating, hasCompletedTutorial, updateUserDetails]);

  const completeTutorial = useCallback(async (tutorialId) => {
    // NEVER write anything while impersonating.
    //
    // LearnMedsender's `if (impersonating) return null` is a RENDER guard, and
    // both hooks (and every effect in them) run before it. So a tutorial could be
    // active in redux — the Guide page's launch card reached this state — and an
    // emitted action result then completed it against the IMPERSONATED user:
    // their localStorage progress, their PostHog events, and a real
    // `has_completed_tutorial: true` PATCH onto their row. The impersonator saw
    // nothing, which is exactly why it went unnoticed. Every writer in this hook
    // is therefore gated here, not at the render.
    if (impersonating) return;
    if (completedThisRunRef.current === tutorialId) return;
    completedThisRunRef.current = tutorialId;

    // Hand the baton across a possible teardown rather than dropping it.
    //
    // The taught action often destroys the page immediately AFTER completing the
    // tutorial: archiving reloads (UploadedRecords), a successful upload
    // navigates. Clearing the marker here — which is what this used to do — meant
    // `finishTutorial({ reopenMenu: true })` below reopened a checklist that the
    // reload then threw away, leaving a bare page. Swapping the "running" marker
    // for a short-lived "just completed" one lets the next load reopen it.
    rememberTutorialCompletion(tutorialId);
    const nextProgress = markTutorialComplete(userEmail, tutorialId);
    if (!mountedRef.current) return;
    setProgressOverride({ userKey, value: nextProgress });
    captureAnalytics('tutorial_completed', { tutorial: tutorialId });

    const allDone = areAllTutorialsComplete(nextProgress, coreTutorials);
    finishTutorial({ reopenMenu: true });

    // Only claim "done" durably if the local record of it actually stored. When
    // localStorage is denied or full, `persisted` is false and this session's
    // checkmarks vanish on the next load — flipping the permanent server flag on
    // that basis would suppress the checklist forever for a user whose progress was
    // never really saved.
    if (allDone && nextProgress.persisted !== false) {
      captureAnalytics('tutorial_all_completed', {
        completed_count: nextProgress.completed.length,
        $set: { has_completed_tutorial: true },
      });
      await persistTutorialCompletion();
    }
  }, [
    impersonating, userEmail, userKey, mountedRef, coreTutorials,
    persistTutorialCompletion, finishTutorial,
  ]);

  /** Arms a fresh run so its completion is recorded once (see completedThisRunRef). */
  const beginTutorialRun = useCallback(() => { completedThisRunRef.current = null; }, []);

  const dismissTutorials = useCallback(async () => {
    if (impersonating) return;
    clearTutorialSession();
    const nextProgress = markTutorialsDismissed(userEmail);
    setProgressOverride({ userKey, value: nextProgress });
    captureAnalytics('tutorial_dismissed', { $set: { has_completed_tutorial: true } });
    closeTutorialMenu();
    await persistTutorialCompletion();
  }, [impersonating, userEmail, userKey, persistTutorialCompletion, closeTutorialMenu]);

  return {
    progress,
    tutorials,
    coreTutorials,
    completedCount,
    completeTutorial,
    beginTutorialRun,
    dismissTutorials,
  };
};

export default useTutorialProgress;
